import {
  HttpBackend,
  HttpClient,
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpInterceptorFn,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, EMPTY, Observable, of, switchMap, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { apiAuthHeaderMap } from '../utils/http-headers.util';

/** When true, this request already ran refresh+retry — do not loop. */
const apiAuthRetryDone = new HttpContextToken<boolean>(() => false);

/** HttpBackend so refresh does not re-enter interceptors. Returns true when a new JWT was stored. */
function refreshAccessToken(backend: HttpBackend): Observable<boolean> {
  const token = localStorage.getItem('jwt_token');
  if (!token) {
    return of(false);
  }
  const raw = new HttpClient(backend);
  return raw
    .post<{ token?: string }>(
      `${environment.apiUrl}/auth/refresh`,
      {},
      {
        headers: apiAuthHeaderMap(),
      }
    )
    .pipe(
      map((res) => {
        if (res?.token) {
          localStorage.setItem('jwt_token', res.token);
          return true;
        }
        return false;
      }),
      catchError(() => of(false))
    );
}

function logoutAndRedirect(): Observable<HttpEvent<unknown>> {
  localStorage.removeItem('jwt_token');
  window.location.href = '/login';
  return EMPTY;
}

/**
 * HTTP interceptor to add JWT token and API key to all API requests
 *
 * For /api/** endpoints, both are required:
 *   - JWT token: User authentication (from Google OAuth)
 *   - API key: Service/client identification
 *
 * On 401, refresh once and retry the same request (HttpContextToken prevents loops).
 * A second 401 clears session and redirects to login.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  // Must resolve in the interceptor body — not inside catchError callbacks (NG0203).
  const httpBackend = inject(HttpBackend);

  try {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return next(req);
    }

    const token = localStorage.getItem('jwt_token');

    const url = req.url.toLowerCase();
    const isApiRequest =
      url.startsWith('/api') ||
      url.startsWith('/api/') ||
      url.includes('/api/') ||
      url.match(/https?:\/\/[^/]+\/api/) ||
      url.match(/\/api\//);

    const isAuthEndpoint =
      isApiRequest &&
      (url.includes('/api/auth/register') ||
        url.includes('/api/auth/login') ||
        url.includes('/api/auth/forgot-password') ||
        url.includes('/api/auth/reset-password') ||
        url.includes('/api/auth/refresh') ||
        url.includes('/api/auth/oauth/native'));

    if (isApiRequest) {
      const existingAuthHeader = req.headers.get('Authorization');
      const existingApiKeyHeader = req.headers.get('api-key');

      const headers: { [key: string]: string } = {};

      if (!existingAuthHeader && token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      if (!existingApiKeyHeader && environment.apiKey) {
        headers['api-key'] = environment.apiKey;
        headers['X-Api-Key'] = environment.apiKey;
      }

      const clonedRequest = req.clone({
        setHeaders: headers,
      });

      const retryAlreadyDone = clonedRequest.context.get(apiAuthRetryDone);

      const handle401 = (error: HttpErrorResponse): Observable<HttpEvent<unknown>> => {
        if (error.status === 403) {
          window.dispatchEvent(new CustomEvent('subscription-required-error'));
          return throwError(() => error);
        }
        if (error.status !== 401) {
          return throwError(() => error);
        }
        if (isAuthEndpoint) {
          return throwError(() => error);
        }
        // One refresh+retry for any API call (uploads, transcription list, etc.) so quick
        // navigation after token expiry does not fail before the user can recover.
        if (!retryAlreadyDone) {
          return refreshAccessToken(httpBackend).pipe(
            switchMap((refreshed): Observable<HttpEvent<unknown>> => {
              const fresh = localStorage.getItem('jwt_token');
              if (!refreshed || !fresh) {
                return logoutAndRedirect();
              }
              const retryReq = clonedRequest.clone({
                context: clonedRequest.context.set(apiAuthRetryDone, true),
                setHeaders: apiAuthHeaderMap(),
              });
              return next(retryReq).pipe(
                catchError((second: HttpErrorResponse): Observable<HttpEvent<unknown>> => {
                  if (second.status === 401) {
                    return logoutAndRedirect();
                  }
                  return throwError(() => second);
                })
              );
            })
          );
        }
        return logoutAndRedirect();
      };

      return next(clonedRequest).pipe(catchError(handle401));
    }

    return next(req);
  } catch {
    return next(req);
  }
};
